import React, { useState } from 'react';
import { Checkbox, Col, Form, Row } from 'antd';
import { CustomInput, CustomParagraph } from '../../../components';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';

interface FormValues {
  name?: string;
  title?: string;
  password?: string;
  phone_number?: string;
  notifications?: string[];
}

/**
 * Account creation form component for user onboarding
 *
 * Handles user registration with validation for name, title, password,
 * optional phone number, and notification preferences
 */
const CreateAccount: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [_isFormValid, setIsFormValid] = useState<boolean>(false);

  const onFinish = async (values: FormValues) => {
    if (values && Object.keys(values).length > 0) {
      setIsFormValid(true);
    }
  };

  return (
    <div className='wrapper-create-account'>
      <Form
        layout='vertical'
        form={form}
        name='validateOnly'
        onFinish={onFinish}
      >
        <CustomParagraph
          text={'Create your account'}
          fontSize='28px'
          fontWeight='700'
          lineHeight='40px'
          marginBottom='32px'
        />
        <Row gutter={[16, 24]}>
          {/* name  */}
          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Your name'
              name='name'
              rules={[
                { required: true, message: 'Please enter your name' },
                { type: 'string', message: 'Please enter a valid name' },
              ]}
            >
              <CustomInput
                placeholder='First and last'
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
              />
            </Form.Item>
          </Col>

          {/* title */}
          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Your title'
              name='title'
              rules={[
                { required: true, message: 'Please enter title' },
                { type: 'string', message: 'Please enter a valid title' },
              ]}
            >
              <CustomInput
                placeholder='e.g. Logistics Coordinator'
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
              />
            </Form.Item>
          </Col>

          {/* password  */}
          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Create a password'
              name='password'
              rules={[
                { required: true, message: 'Please enter your password' },
                { type: 'string', message: 'Please enter a valid password' },
                {
                  type: 'string',
                  min: 6,
                  message: 'Password must be at least 6 characters long.',
                },
                {
                  type: 'string',
                  max: 20,
                  message: 'Password cannot exceed 20 characters.',
                },
              ]}
            >
              <CustomInput
                placeholder=''
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
                type='password'
              />
            </Form.Item>
          </Col>

          {/* phone number  */}
          <Col span={24}>
            <div className='my-2'>
              <Form.Item
                label={'Phone number (optional)'}
                name='phone_number'
                style={{ marginBottom: '0px' }}
                initialValue=''
                rules={[
                  {
                    validator: (_, value: string) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve(); // Optional
                      }
                      try {
                        if (isValidPhoneNumber(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Invalid phone number')
                          );
                        }
                      } catch {
                        return Promise.reject(
                          new Error('Invalid phone number')
                        );
                      }
                    },
                  },
                ]}
              >
                <PhoneInput
                  country={'us'}
                  value={form.getFieldValue('phone_number')}
                  onChange={(value, countryData, e, formattedValue: string) => {
                    form.setFieldsValue({ phone_number: formattedValue });
                  }}
                />
              </Form.Item>
            </div>
          </Col>

          {/* notifications medium  */}
          <Col span={24}>
            <Form.Item
              label='Opt in for notifications via...'
              name='notifications'
            >
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <Checkbox value='sms'>SMS</Checkbox>
                <Checkbox value='whatsapp'>WhatsApp</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CreateAccount;
